import { useMemo, useState } from 'react';
import {
  portalWiConfirm, portalWiDispute, portalWiDocumentUrl, apiErrText, wiMoney, wiRate,
  TIER_LABEL, WI_PRICE_CONFIRMATION_STATES,
  type PortalWiDetail, type PortalWiPriceConfirmation, type PortalWiSnapshotLine,
  type PortalWiTier, type PortalWiComponent,
} from '../lib/api';
import { toast } from './ui/Toast';

/**
 * 认价单 / price confirmation — 乙方 countersigns WTL's audited figure, or says why not. Eric 9/18 (工程指令).
 *
 * G9 is the reason this card is a WORKING, not a total: 「审核价 ¥X, 请确认」 is not a thing a contractor can
 * check, and an unconfirmable number gets argued about at 结算 instead of here. So every line shows 申报 beside
 * 审核, the tier that decided it, and `steps[]` — the server's own arithmetic, line by line, in the order it
 * was performed (base → 下浮 10% → 管理费利润 3% → × 数量).
 *
 * `expected_total_rmb` binds consent to the figure ON THIS SCREEN (the confirm-final precedent,
 * portal.ts:386). If 预算 re-priced while this card sat open on a phone, the server answers 409
 * `total_changed` and the card refetches rather than locking the contractor into a number they never saw.
 *
 * `policy_label` is rendered verbatim as a footnote: 认价 is a WTL process, not a contract term, and the UI
 * must not imply otherwise (§8 risk 4 — nothing here may look like an enforcement).
 */

/**
 * Read the stored snapshot defensively — it is a blob PR3 wrote, not a typed row. PR3 nests the G9
 * arithmetic under `pricing:{steps,components}` (same shape as the live line), so it is flattened here
 * and the flat form is still accepted: the card must render whichever the version on disk happens to be.
 */
function snapshotLines(pc: PortalWiPriceConfirmation): PortalWiSnapshotLine[] {
  const s = pc.snapshot as Record<string, unknown> | null;
  if (!s) return [];
  const raw = Array.isArray(s) ? s : (s.lines as unknown);
  if (!Array.isArray(raw)) return [];
  return (raw as PortalWiSnapshotLine[]).map((l) => ({
    ...l,
    steps: l.steps ?? l.pricing?.steps ?? [],
    components: l.components ?? l.pricing?.components ?? [],
    tier: l.tier ?? (l.pricing?.tier as PortalWiTier | null | undefined) ?? null,
  }));
}
function tierText(t: string | null | undefined): string | null {
  return t && t in TIER_LABEL ? TIER_LABEL[t as PortalWiTier] : null;
}
function componentText(c: PortalWiComponent): string {
  const bits = [c.label ?? c.kind ?? '', c.qty != null ? `${c.qty}${c.unit ?? ''}` : '', c.unit_price_rmb != null ? `× ${wiRate(c.unit_price_rmb)}` : ''];
  const tail = c.median_of?.length ? `（中位数 / median of ${c.median_of.join('、')}）` : c.source ? `（${c.source}）` : '';
  return `${bits.filter(Boolean).join(' ')}${tail}`;
}

export default function PortalPriceConfirmCard({
  eid, detail, onRefetch,
}: {
  eid: string;
  detail: PortalWiDetail;
  onRefetch: () => void;
}) {
  const wi = detail.instruction;
  const pc = detail.price_confirmation;
  const [busy, setBusy] = useState<'confirm' | 'dispute' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [disputing, setDisputing] = useState(false);
  const [note, setNote] = useState('');
  const [confirmNote, setConfirmNote] = useState('');

  // The snapshot is what they are asked to agree to; the live lines are the fallback before PR3 has
  // written one (a card that renders nothing is worse than a card that renders today's figures).
  const lines = useMemo<PortalWiSnapshotLine[]>(() => {
    const snap = pc ? snapshotLines(pc) : [];
    if (snap.length) return snap;
    return detail.lines.map((l) => ({
      line_id: l.id, seq: l.seq, item_label: l.item_label, spec_md: l.spec_md, unit: l.unit, qty: l.qty,
      declared_unit_rate_rmb: l.declared_unit_rate_rmb, declared_amount_rmb: l.declared_amount_rmb,
      audited_unit_rate_rmb: l.audited_unit_rate_rmb, audited_amount_rmb: l.audited_amount_rmb,
      tier: l.tier, adjustment_note_md: l.adjustment_note_md,
      steps: l.pricing?.steps ?? [], components: l.pricing?.components ?? [],
    }));
  }, [pc, detail.lines]);

  if (!pc && !detail.lines.some((l) => l.audited_unit_rate_rmb != null)) return null;

  const confirmed = !!wi.confirmed_at || pc?.status === 'confirmed';
  const disputed = !!wi.disputed_at || pc?.status === 'disputed';
  const total = pc?.total_audited_rmb ?? null;
  const canAct = wi.state === 'approved' && !confirmed;
  const docReady = WI_PRICE_CONFIRMATION_STATES.includes(wi.state);

  async function confirm() {
    if (total == null) { setErr('认价金额尚未出具，请稍后刷新。/ the audited total is not available yet — refresh in a moment.'); return; }
    setBusy('confirm'); setErr(null);
    try {
      await portalWiConfirm(eid, wi.id, total, confirmNote.trim() || undefined);
      onRefetch();
      toast('已认价，感谢确认。/ Confirmed — thank you.', { kind: 'success' });
    } catch (e) {
      // 409 total_changed: 预算 re-priced under us. Pull the new figure in rather than retrying blind.
      const msg = apiErrText(e);
      setErr((e as { body?: { error?: string } })?.body?.error === 'total_changed'
        ? '认价金额已更新，请刷新后再确认。/ The confirmed total changed — refresh and confirm again.'
        : msg);
      onRefetch();
    } finally { setBusy(null); }
  }
  async function dispute() {
    if (!note.trim()) return;
    setBusy('dispute'); setErr(null);
    try {
      await portalWiDispute(eid, wi.id, note.trim());
      setNote(''); setDisputing(false);
      onRefetch();
      toast('异议已提交，预算会重新核价。/ Objection filed — WTL will re-price it.', { kind: 'success' });
    } catch (e) {
      setErr(apiErrText(e));
    } finally { setBusy(null); }
  }

  return (
    <div className="card" style={{ marginTop: 10, borderLeft: `3px solid ${confirmed ? 'var(--good)' : disputed ? 'var(--bad)' : 'var(--accent)'}` }}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong style={{ flex: 1, minWidth: 140 }}>
          认价单 / Price confirmation{pc ? <span className="muted small"> · 第 {pc.version} 版 / v{pc.version}</span> : null}
        </strong>
        {confirmed && <span className="pill small pill-good">✓ 已认价 / Confirmed</span>}
        {disputed && !confirmed && <span className="pill small pill-bad">劳务有异议 / Disputed</span>}
      </div>

      {confirmed && (
        <div className="small" style={{ color: 'var(--good)', marginTop: 4 }}>
          ✓ 已认价 · {String(pc?.labor_confirmed_at ?? wi.confirmed_at ?? '').slice(0, 16).replace('T', ' ')}
          {total != null && <> · {wiMoney(total)}</>}
        </div>
      )}
      {disputed && !confirmed && (
        <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>
          已提交异议 {String(pc?.labor_disputed_at ?? wi.disputed_at ?? '').slice(0, 16).replace('T', ' ')}
          {(pc?.dispute_note_md || wi.dispute_note_md) && <>：{pc?.dispute_note_md ?? wi.dispute_note_md}</>}
          <div className="muted">预算会重新核价，新的认价单出具后会在这里出现。/ WTL is re-pricing — a new version will appear here.</div>
        </div>
      )}

      {/* ── 申报价 vs 审核价, line by line ─────────────────────────────────────────────────────────
          Stacked, NOT a table: five money columns cannot be read on a 375px phone without the page
          scrolling sideways, and a 认价单 the contractor has to pan across is a 认价单 they will sign
          without checking. Each line is its own block, 申报 above 审核 so the movement is obvious. */}
      <div style={{ marginTop: 8 }}>
        {lines.map((l, i) => (
          <div key={l.line_id ?? l.id ?? i} style={{ padding: '8px 0', borderTop: '1px solid var(--rule)' }}>
            <div className="small"><strong>{i + 1}. {l.item_label ?? '—'}</strong></div>
            {l.spec_md && <div className="muted small">{l.spec_md}</div>}
            <div className="muted small">工程量 / qty：{l.qty ?? '—'} {l.unit ?? ''}</div>
            {tierText(l.tier) && <div className="muted small" style={{ marginTop: 2 }}>{tierText(l.tier)}</div>}
            {l.adjustment_note_md && <div className="muted small">说明 / basis：{l.adjustment_note_md}</div>}
            {(l.components ?? []).length > 0 && (
              <div className="muted small" style={{ marginTop: 2 }}>
                组价 / build-up：{(l.components ?? []).map((c, j) => <div key={j}>· {componentText(c)}</div>)}
              </div>
            )}
            {(l.steps ?? []).length > 0 && (
              <div className="muted small" style={{ marginTop: 2 }}>
                算式 / steps：{(l.steps ?? []).map((s, j) => <div key={j}>{j + 1}) {s}</div>)}
              </div>
            )}
            <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline', marginTop: 4 }}>
              <span className="small muted" style={{ flex: '1 1 130px' }}>申报单价 / declared {wiRate(l.declared_unit_rate_rmb)}</span>
              <span className="small" style={{ flex: '1 1 130px' }}>
                审核单价 / audited <strong>{wiRate(l.audited_unit_rate_rmb)}</strong>
              </span>
            </div>
            <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline' }}>
              <span className="small" style={{ flex: 1, minWidth: 120 }}>审核合价 / amount</span>
              <strong className="small">{wiMoney(l.audited_amount_rmb)}</strong>
            </div>
          </div>
        ))}
      </div>

      {/* ── 合计 ───────────────────────────────────────────────────────────────────────────────── */}
      {pc && (
        <div className="small" style={{ marginTop: 8, borderTop: '1px solid var(--rule)', paddingTop: 6 }}>
          <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline' }}>
            <span style={{ flex: 1, minWidth: 120 }}>申报合计 / Declared total</span>
            <span>{wiMoney(pc.total_declared_rmb)}</span>
          </div>
          <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline' }}>
            <span style={{ flex: 1, minWidth: 120 }}><strong>审核合计 / Audited total</strong></span>
            <strong className="money-lg" style={{ fontSize: 16 }}>{wiMoney(pc.total_audited_rmb)}</strong>
          </div>
          {(pc.additions_rmb != null || pc.omissions_rmb != null) && (
            <div className="muted small">
              增项 / additions {wiMoney(pc.additions_rmb)} · 减项 / omissions {wiMoney(pc.omissions_rmb)}
            </div>
          )}
          {pc.approved_at && <div className="muted small">商务批准 / approved {pc.approved_at.slice(0, 16).replace('T', ' ')}</div>}
        </div>
      )}

      {/* 「公司要求，非合同条款」 — verbatim, because it is the honest description of what 认价 is. */}
      {pc?.policy_label && <div className="muted small" style={{ marginTop: 6 }}>※ {pc.policy_label}</div>}

      {docReady && (
        <div style={{ marginTop: 6 }}>
          <a className="small" href={portalWiDocumentUrl(eid, wi.id, 'price_confirmation')}>⬇ 认价单 Word / Price confirmation (.docx)</a>
        </div>
      )}

      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}

      {canAct && (
        <div style={{ marginTop: 8, borderTop: '1px solid var(--rule)', paddingTop: 8 }}>
          {!disputing ? (
            <>
              <div className="row-wrap" style={{ gap: 6, alignItems: 'center' }}>
                <input
                  className="grow" style={{ minWidth: 140 }} value={confirmNote} disabled={!!busy}
                  onChange={(e) => setConfirmNote(e.target.value)} placeholder="备注（选填）/ note (optional)"
                />
              </div>
              <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 6 }}>
                <button className="btn-good" disabled={!!busy || total == null} onClick={() => void confirm()}>
                  {busy === 'confirm' ? '确认中… / confirming…' : `✓ 确认认价 / Confirm ${total == null ? '' : wiMoney(total)}`}
                </button>
                <button disabled={!!busy} onClick={() => setDisputing(true)}>✎ 有异议（填写理由）/ Object</button>
              </div>
              <div className="muted small" style={{ marginTop: 4 }}>
                确认即表示同意上表的审核金额。/ Confirming means you accept the audited figures above.
              </div>
            </>
          ) : (
            <>
              <div className="small"><strong>异议理由（必填）/ Reason for the objection (required)</strong></div>
              <textarea
                rows={3} style={{ width: '100%', marginTop: 4 }} value={note} disabled={!!busy}
                onChange={(e) => setNote(e.target.value)}
                placeholder="请说明哪一行、哪一个单价有异议，以及您的依据 / say which line and which rate, and on what basis"
              />
              <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 6 }}>
                <button className="btn-primary" disabled={!!busy || !note.trim()} onClick={() => void dispute()}>
                  {busy === 'dispute' ? '提交中… / filing…' : '提交异议 / File the objection'}
                </button>
                <button className="small" disabled={!!busy} onClick={() => { setDisputing(false); setNote(''); }}>取消 / Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
